import { observer } from 'mobx-react-lite'
import React, { useMemo } from 'react'
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Area,
} from 'recharts'
import { formatSecondsToLargestUnit } from 'shared/lib/dayjs'
import {
  PERFORMANCE_METRIC_KEYS,
  PERFORMANCE_METRICS_CONFIG,
} from 'entities/Analytics/lib/constants'
import type { PerformanceChartDataStore } from 'pages/analytics/pages/performance/ui/Chart/store/performanceChartDataStore'
import {
  Y_AXIS_LABEL_CHAR_WIDTH_PX,
  Y_AXIS_LABEL_MIN_WIDTH_PX,
  Y_AXIS_LABEL_PADDING_PX,
} from 'pages/analytics/constants/constants'
import styles from '../styles.module.scss'

interface PerformanceLineChartProps {
  chartDataStore: PerformanceChartDataStore
  hiddenMetrics?: Set<string>
}

export const PerformanceLineChart = observer(
  ({ chartDataStore, hiddenMetrics = new Set() }: PerformanceLineChartProps) => {
    const { rechartsData } = chartDataStore

    const totalMetrics = Object.keys(PERFORMANCE_METRICS_CONFIG).length
    const isOneVisibleMetric = hiddenMetrics.size === totalMetrics - 1
    const visibleMetricColor = isOneVisibleMetric
      ? Object.entries(PERFORMANCE_METRICS_CONFIG).find(([key]) => !hiddenMetrics.has(key))?.[1]
          ?.color
      : ''

    const formatYAxisTick = (value: number) => formatSecondsToLargestUnit(value)
    const formatTooltipValue = (value: number, name: string) => [
      formatSecondsToLargestUnit(value),
      name,
    ]

    const yAxisWidth = useMemo(() => {
      const labelLengths = rechartsData.flatMap((row) =>
        PERFORMANCE_METRIC_KEYS.map((key) => {
          const value = Number(row[key] ?? 0)
          return String(formatSecondsToLargestUnit(value)).length
        })
      )
      const maxLabelLengths = labelLengths.length ? Math.max(...labelLengths) : 0
      return Math.max(
        Y_AXIS_LABEL_MIN_WIDTH_PX,
        maxLabelLengths * Y_AXIS_LABEL_CHAR_WIDTH_PX + Y_AXIS_LABEL_PADDING_PX
      )
    }, [rechartsData])

    return (
      <div className={styles.chartContainer}>
        <ResponsiveContainer width='100%' height={300}>
          <ComposedChart data={rechartsData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            {isOneVisibleMetric && (
              <defs>
                <linearGradient id='colorGradient' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='0%' stopColor={visibleMetricColor} stopOpacity={0.3} />
                  <stop offset='100%' stopColor={visibleMetricColor} stopOpacity={0.05} />
                </linearGradient>
              </defs>
            )}
            <CartesianGrid strokeDasharray='3 3' vertical={false} />
            <XAxis dataKey='name' interval='preserveStartEnd' minTickGap={1} />
            <YAxis tickFormatter={formatYAxisTick} width={yAxisWidth} />
            <Tooltip cursor={false} formatter={formatTooltipValue} />
            {Object.entries(PERFORMANCE_METRICS_CONFIG).map(([key, config]) => {
              if (hiddenMetrics.has(key)) return null

              return (
                <g key={key}>
                  {isOneVisibleMetric && (
                    <Area
                      type='monotone'
                      dataKey={key}
                      stroke={config.color}
                      fill='url(#colorGradient)'
                      strokeWidth={0}
                      tooltipType='none'
                    />
                  )}
                  <Line
                    type='monotone'
                    dataKey={key}
                    stroke={config.color}
                    strokeWidth={2}
                    name={config.name}
                    dot={false}
                  />
                </g>
              )
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    )
  }
)
